import type {
  SocialListeningAnalysisDetails,
  SocialListeningHistoryItem,
  SocialListeningSourceBreakdown,
} from "src/services/SocialListeningAPIs";

export const DEMO_SOCIAL_LISTENING_IDS = [-1, -2, -3, -4, -5] as const;

type DemoLang = "en" | "ar";

const resolveLang = (language: string | undefined): DemoLang =>
  (language ?? "en").toLowerCase().startsWith("ar") ? "ar" : "en";

interface DemoSourceSeed {
  source_name: string;
  total_posts: number;
  total_comments: number;
  positive_count: number;
  neutral_count: number;
  negative_count: number;
  key_themes: { en: string[]; ar: string[] };
  top_positive_points: { en: string[]; ar: string[] };
  top_negative_points: { en: string[]; ar: string[] };
  posts: Array<{
    type: string;
    likes: number;
    details: { en: string; ar: string };
    comments: { en: string[]; ar: string[] };
  }>;
}

interface DemoReportSeed {
  id: number;
  companyName: { en: string; ar: string };
  reportTitle: { en: string; ar: string };
  analysisType: "brand_analysis" | "competitive_analysis";
  daysAgo: number;
  dataQualityScore: number;
  sentiment: {
    total_posts: number;
    total_texts: number;
    positive_count: number;
    neutral_count: number;
    negative_count: number;
  };
  sources: DemoSourceSeed[];
  /** brand_analysis reports */
  narrative?: { en: string; ar: string };
  highlights?: { en: string[]; ar: string[] };
  recommendedActions?: { en: string[]; ar: string[] };
  /** competitive_analysis reports */
  llmComparison?: {
    executive_summary: { en: string; ar: string };
    comparison_posts: { en: string[]; ar: string[] };
    comparison_comments: { en: string[]; ar: string[] };
    comparison_metrics: { en: string[]; ar: string[] };
    risks: { en: string[]; ar: string[] };
    opportunities: { en: string[]; ar: string[] };
    recommendations: { en: string[]; ar: string[] };
  };
}

const pct = (count: number, total: number) =>
  total > 0 ? Math.round((count / total) * 1000) / 10 : 0;

const STC_INSTAGRAM: DemoSourceSeed = {
  source_name: "Instagram",
  total_posts: 214,
  total_comments: 1862,
  positive_count: 138,
  neutral_count: 52,
  negative_count: 24,
  key_themes: {
    en: ["5G network speed", "customer service wait times", "new eSIM launch"],
    ar: ["سرعة شبكة الجيل الخامس", "أوقات انتظار خدمة العملاء", "إطلاق شريحة eSIM الجديدة"],
  },
  top_positive_points: {
    en: [
      "Widely praised 5G coverage expansion in Riyadh and Jeddah",
      "eSIM rollout seen as a convenient, modern upgrade",
    ],
    ar: [
      "إشادة واسعة بتوسع تغطية الجيل الخامس في الرياض وجدة",
      "يُنظر إلى إطلاق شريحة eSIM كترقية عصرية ومريحة",
    ],
  },
  top_negative_points: {
    en: [
      "Recurring complaints about call center hold times",
      "Billing app glitches reported after the latest update",
    ],
    ar: [
      "شكاوى متكررة حول أوقات الانتظار في مركز الاتصال",
      "بلاغات عن أعطال في تطبيق الفواتير بعد آخر تحديث",
    ],
  },
  posts: [
    {
      type: "post",
      likes: 1420,
      details: {
        en: "stc's new eSIM activation flow is genuinely fast — had my new line running in under two minutes.",
        ar: "عملية تفعيل شريحة eSIM الجديدة من STC سريعة فعلاً — فعّلت خطي الجديد في أقل من دقيقتين.",
      },
      comments: {
        en: ["Same experience here, no store visit needed.", "Wish other carriers matched this."],
        ar: ["نفس تجربتي، بدون الحاجة لزيارة فرع.", "ليت باقي الشركات توفر هذا."],
      },
    },
    {
      type: "post",
      likes: 96,
      details: {
        en: "Been on hold with stc support for 40 minutes about a billing error. Still unresolved.",
        ar: "أنتظر على الخط مع دعم STC منذ ٤٠ دقيقة بخصوص خطأ في الفاتورة، ولم يُحل بعد.",
      },
      comments: {
        en: ["Try the app chat instead, faster for me.", "Same issue last month, took 3 days."],
        ar: ["جرب المحادثة عبر التطبيق، كانت أسرع بالنسبة لي.", "واجهت نفس المشكلة الشهر الماضي واستغرقت ٣ أيام."],
      },
    },
  ],
};

const STC_TWITTER: DemoSourceSeed = {
  source_name: "X (Twitter)",
  total_posts: 341,
  total_comments: 2210,
  positive_count: 190,
  neutral_count: 98,
  negative_count: 53,
  key_themes: {
    en: ["network reliability in Eastern Province", "data plan pricing", "app redesign"],
    ar: ["موثوقية الشبكة في المنطقة الشرقية", "أسعار باقات البيانات", "إعادة تصميم التطبيق"],
  },
  top_positive_points: {
    en: ["Praise for consistent coverage on major highways", "Positive reaction to the new app dashboard"],
    ar: ["إشادة بثبات التغطية على الطرق السريعة الرئيسية", "تفاعل إيجابي مع لوحة التطبيق الجديدة"],
  },
  top_negative_points: {
    en: ["Requests for cheaper mid-tier data plans", "Occasional signal drops reported in Dammam"],
    ar: ["مطالبات بباقات بيانات متوسطة أرخص", "بلاغات متفرقة عن انقطاع الإشارة في الدمام"],
  },
  posts: [
    {
      type: "post",
      likes: 512,
      details: {
        en: "Drove Riyadh to Dammam today, stc signal never dropped once. Solid.",
        ar: "قدت اليوم من الرياض إلى الدمام ولم تنقطع إشارة STC ولو لمرة. ممتازة.",
      },
      comments: {
        en: ["Good to know, was worried about the route.", "My experience too on that road."],
        ar: ["جيد أن أعرف ذلك، كنت قلقاً بشأن هذا الطريق.", "نفس تجربتي في هذا الطريق."],
      },
    },
  ],
};

const ALMARAI_FACEBOOK: DemoSourceSeed = {
  source_name: "Facebook",
  total_posts: 176,
  total_comments: 1340,
  positive_count: 112,
  neutral_count: 44,
  negative_count: 20,
  key_themes: {
    en: ["Ramadan product bundles", "dairy freshness", "packaging redesign"],
    ar: ["عروض منتجات رمضان", "طازجية منتجات الألبان", "إعادة تصميم العبوات"],
  },
  top_positive_points: {
    en: ["Ramadan bundle pricing seen as good value", "Consistent praise for milk freshness and taste"],
    ar: ["أسعار عروض رمضان تُعتبر ذات قيمة جيدة", "إشادة مستمرة بطازجية وطعم الحليب"],
  },
  top_negative_points: {
    en: ["Some complaints about juice carton leaks", "Requests for more sugar-free product options"],
    ar: ["بعض الشكاوى حول تسرب عبوات العصير", "مطالبات بمزيد من خيارات المنتجات الخالية من السكر"],
  },
  posts: [
    {
      type: "post",
      likes: 860,
      details: {
        en: "Almarai's Ramadan family pack saved us so much this year, great value for the quality.",
        ar: "عبوة رمضان العائلية من المراعي وفّرت علينا الكثير هذا العام، قيمة رائعة مقابل الجودة.",
      },
      comments: {
        en: ["Bought two, kids loved the juice.", "Best value dairy bundle this Ramadan."],
        ar: ["اشتريت عبوتين، والأطفال أحبوا العصير.", "أفضل عرض ألبان هذا رمضان من ناحية القيمة."],
      },
    },
    {
      type: "post",
      likes: 34,
      details: {
        en: "Second juice carton this month that arrived leaking from the seal.",
        ar: "ثاني عبوة عصير هذا الشهر تصلني وهي تسرب من مكان الإغلاق.",
      },
      comments: {
        en: ["Happened to me too, contacted their support.", "Check the batch number, might be one lot."],
        ar: ["حدث معي أيضاً، تواصلت مع الدعم.", "تحقق من رقم الدفعة، ربما إنتاج دفعة واحدة."],
      },
    },
  ],
};

const ALBAIK_INSTAGRAM: DemoSourceSeed = {
  source_name: "Instagram",
  total_posts: 298,
  total_comments: 3120,
  positive_count: 231,
  neutral_count: 48,
  negative_count: 19,
  key_themes: {
    en: ["signature garlic sauce", "weekend queue length", "new Jeddah branch"],
    ar: ["صوص الثوم المميز", "طول الطابور في نهاية الأسبوع", "فرع جدة الجديد"],
  },
  top_positive_points: {
    en: ["Garlic sauce remains the single most-mentioned favorite", "Strong excitement around the new branch opening"],
    ar: ["صوص الثوم يبقى الأكثر ذكراً كمفضل لدى الجمهور", "حماس كبير حول افتتاح الفرع الجديد"],
  },
  top_negative_points: {
    en: ["Long drive-thru queues on Thursday/Friday evenings", "Delivery orders sometimes arriving cold"],
    ar: ["طوابير طويلة في السيارات مساء الخميس والجمعة", "وصول بعض طلبات التوصيل باردة أحياناً"],
  },
  posts: [
    {
      type: "post",
      likes: 2340,
      details: {
        en: "Nothing beats Al Baik garlic sauce, I'd drive an hour just for that alone.",
        ar: "لا شيء يضاهي صوص الثوم من البيك، أقود سيارتي ساعة كاملة من أجله فقط.",
      },
      comments: {
        en: ["Facts, it's the best in the country.", "New branch better have enough sauce packets!"],
        ar: ["صحيح، إنه الأفضل في المملكة.", "أتمنى الفرع الجديد يوفر كمية كافية من الصوص!"],
      },
    },
    {
      type: "post",
      likes: 41,
      details: {
        en: "Waited 35 minutes in the drive-thru Thursday night, food was still good though.",
        ar: "انتظرت ٣٥ دقيقة في طابور السيارات مساء الخميس، لكن الطعام كان لذيذاً رغم ذلك.",
      },
      comments: {
        en: ["Thursday nights are always like this there.", "Try the app for pickup, much faster."],
        ar: ["ليالي الخميس دائماً هكذا هناك.", "جرب التطبيق للاستلام، أسرع بكثير."],
      },
    },
  ],
};

const HERFY_INSTAGRAM: DemoSourceSeed = {
  source_name: "Instagram",
  total_posts: 187,
  total_comments: 1490,
  positive_count: 121,
  neutral_count: 46,
  negative_count: 20,
  key_themes: {
    en: ["value meal pricing", "loyalty app rewards", "burger consistency"],
    ar: ["أسعار الوجبات الاقتصادية", "مكافآت تطبيق الولاء", "ثبات جودة البرغر"],
  },
  top_positive_points: {
    en: ["Value meals seen as the most affordable option in category", "Loyalty app points redemption praised as simple"],
    ar: ["الوجبات الاقتصادية تُعتبر الأرخص في فئتها", "سهولة استبدال نقاط تطبيق الولاء تحظى بإشادة"],
  },
  top_negative_points: {
    en: ["Some reports of inconsistent portion sizes between branches", "Requests for more late-night branch hours"],
    ar: ["بلاغات عن تفاوت حجم الوجبات بين الفروع", "مطالبات بساعات عمل أطول في الفروع ليلاً"],
  },
  posts: [
    {
      type: "post",
      likes: 470,
      details: {
        en: "Herfy's value meal is still the best price-to-portion ratio in Riyadh, honestly.",
        ar: "وجبة هيرفي الاقتصادية لا تزال الأفضل من ناحية السعر مقابل الكمية في الرياض، بصراحة.",
      },
      comments: {
        en: ["Their loyalty points add up fast too.", "Agree, great for a quick lunch."],
        ar: ["نقاط الولاء أيضاً تتجمع بسرعة.", "أوافق، ممتازة لغداء سريع."],
      },
    },
  ],
};

const JARIR_TWITTER: DemoSourceSeed = {
  source_name: "X (Twitter)",
  total_posts: 203,
  total_comments: 1670,
  positive_count: 140,
  neutral_count: 45,
  negative_count: 18,
  key_themes: {
    en: ["back-to-school laptop deals", "in-store tech support", "online delivery speed"],
    ar: ["عروض أجهزة اللابتوب لموسم المدارس", "الدعم الفني داخل الفروع", "سرعة التوصيل الإلكتروني"],
  },
  top_positive_points: {
    en: ["Back-to-school laptop bundle pricing widely praised", "In-store tech support called knowledgeable and fast"],
    ar: ["إشادة واسعة بأسعار عروض اللابتوب لموسم المدارس", "وصف الدعم الفني داخل الفروع بالمعرفة والسرعة"],
  },
  top_negative_points: {
    en: ["Some online orders taking longer than the promised delivery window", "Stock-out complaints on popular GPU models"],
    ar: ["تأخر بعض الطلبات الإلكترونية عن موعد التوصيل الموعود", "شكاوى نفاد المخزون لبعض بطاقات الشاشة الرائجة"],
  },
  posts: [
    {
      type: "post",
      likes: 610,
      details: {
        en: "Jarir's laptop bundle deal for my daughter's school this year was honestly unbeatable.",
        ar: "عرض حزمة اللابتوب من جرير لمدرسة ابنتي هذا العام كان بصراحة لا يُضاهى.",
      },
      comments: {
        en: ["Got the same one, comes with a backpack too.", "Staff helped me pick the right specs."],
        ar: ["اشتريت نفس العرض، يأتي مع حقيبة أيضاً.", "ساعدني الموظف في اختيار المواصفات المناسبة."],
      },
    },
    {
      type: "post",
      likes: 27,
      details: {
        en: "Ordered a graphics card online, delivery took 6 days instead of the promised 2.",
        ar: "طلبت بطاقة شاشة عبر الإنترنت، واستغرق التوصيل ٦ أيام بدلاً من اليومين الموعودين.",
      },
      comments: {
        en: ["High-demand items are often backordered.", "In-store pickup avoided this for me."],
        ar: ["المنتجات كثيرة الطلب غالباً ما تكون متأخرة الشحن.", "الاستلام من الفرع تجنّبني هذه المشكلة."],
      },
    },
  ],
};

const EXTRA_FACEBOOK: DemoSourceSeed = {
  source_name: "Facebook",
  total_posts: 154,
  total_comments: 1128,
  positive_count: 94,
  neutral_count: 40,
  negative_count: 20,
  key_themes: {
    en: ["appliance installment plans", "warranty claims", "White Friday deals"],
    ar: ["خطط تقسيط الأجهزة", "مطالبات الضمان", "عروض الجمعة البيضاء"],
  },
  top_positive_points: {
    en: ["Installment plans on large appliances seen as accessible", "White Friday discounts on TVs called competitive"],
    ar: ["خطط التقسيط على الأجهزة الكبيرة تُعتبر ميسّرة", "خصومات الجمعة البيضاء على التلفزيونات وُصفت بالتنافسية"],
  },
  top_negative_points: {
    en: ["Warranty claim processing described as slow", "Delivery scheduling complaints for large items"],
    ar: ["وصف معالجة مطالبات الضمان بالبطء", "شكاوى حول جدولة توصيل الأجهزة الكبيرة"],
  },
  posts: [
    {
      type: "post",
      likes: 205,
      details: {
        en: "extra's installment plan made the new washing machine actually affordable this month.",
        ar: "خطة تقسيط اكسترا جعلت الغسالة الجديدة في متناول اليد فعلاً هذا الشهر.",
      },
      comments: {
        en: ["Same, no interest for 6 months helped a lot.", "Good deal, checked prices elsewhere first."],
        ar: ["نفس الأمر، بدون فوائد لمدة ٦ أشهر ساعدني كثيراً.", "عرض جيد، قارنت الأسعار في أماكن أخرى أولاً."],
      },
    },
  ],
};

const buildSourceBreakdown = (seed: DemoSourceSeed, lang: DemoLang): SocialListeningSourceBreakdown => {
  const total = seed.positive_count + seed.neutral_count + seed.negative_count;
  return {
    source_name: seed.source_name,
    total_posts: seed.total_posts,
    total_comments: seed.total_comments,
    sentiment_data: {
      positive_count: seed.positive_count,
      positive_percentage: pct(seed.positive_count, total),
      neutral_count: seed.neutral_count,
      neutral_percentage: pct(seed.neutral_count, total),
      negative_count: seed.negative_count,
      negative_percentage: pct(seed.negative_count, total),
      total_count: total,
    },
    key_themes: seed.key_themes[lang],
    top_positive_points: seed.top_positive_points[lang],
    top_negative_points: seed.top_negative_points[lang],
    posts: seed.posts.map((post, index) => ({
      post_id: `${seed.source_name}-${index}`,
      post_details: post.details[lang],
      type: post.type,
      recent_comments: post.comments[lang],
      likes_count: post.likes,
      engagement: { likes: post.likes, comments: post.comments[lang].length, shares: Math.round(post.likes * 0.04) },
    })),
  };
};

const REPORT_SEEDS: DemoReportSeed[] = [
  {
    id: -1,
    companyName: { en: "stc", ar: "STC" },
    reportTitle: { en: "stc Brand Analysis", ar: "تحليل علامة STC التجارية" },
    analysisType: "brand_analysis",
    daysAgo: 2,
    dataQualityScore: 92,
    sentiment: { total_posts: 555, total_texts: 4072, positive_count: 328, neutral_count: 150, negative_count: 77 },
    sources: [STC_INSTAGRAM, STC_TWITTER],
    narrative: {
      en: "Conversation around stc over the past 30 days skews strongly positive, driven by the new eSIM launch and continued praise for 5G coverage across major cities. Customer service response time remains the most consistent friction point, concentrated in call-center wait times rather than in-app or in-store experiences.",
      ar: "تميل النقاشات حول STC خلال آخر ٣٠ يوماً بشكل إيجابي واضح، مدفوعة بإطلاق شريحة eSIM الجديدة واستمرار الإشادة بتغطية الجيل الخامس في المدن الرئيسية. لا يزال وقت استجابة خدمة العملاء أبرز نقطة احتكاك، وتتركز في أوقات انتظار مركز الاتصال أكثر من تجربة التطبيق أو الفروع.",
    },
    highlights: {
      en: [
        "eSIM launch drove a 3.2x spike in positive mentions during its first week.",
        "5G reliability on intercity routes (Riyadh–Dammam, Riyadh–Jeddah) is a recurring positive theme.",
        "Call-center hold times account for over 60% of negative sentiment in the sample.",
      ],
      ar: [
        "أدى إطلاق eSIM إلى ارتفاع الإشارات الإيجابية بمقدار ٣.٢ ضعف خلال أسبوعه الأول.",
        "موثوقية الجيل الخامس على الطرق بين المدن (الرياض-الدمام، الرياض-جدة) موضوع إيجابي متكرر.",
        "تشكّل أوقات انتظار مركز الاتصال أكثر من ٦٠٪ من المشاعر السلبية في العينة.",
      ],
    },
    recommendedActions: {
      en: [
        "Promote eSIM self-activation more heavily; it's outperforming other campaigns organically.",
        "Investigate call-center staffing during peak billing-cycle days to reduce hold times.",
        "Publish a public status page for the billing app to pre-empt update-related complaints.",
      ],
      ar: [
        "الترويج بشكل أكبر للتفعيل الذاتي لشريحة eSIM، فهي تتفوق عضوياً على الحملات الأخرى.",
        "دراسة توزيع الموظفين في مركز الاتصال خلال أيام ذروة دورة الفوترة لتقليل أوقات الانتظار.",
        "نشر صفحة حالة عامة لتطبيق الفواتير لتفادي الشكاوى المرتبطة بالتحديثات.",
      ],
    },
  },
  {
    id: -2,
    companyName: { en: "Almarai", ar: "المراعي" },
    reportTitle: { en: "Almarai Brand Analysis", ar: "تحليل علامة المراعي التجارية" },
    analysisType: "brand_analysis",
    daysAgo: 5,
    dataQualityScore: 89,
    sentiment: { total_posts: 342, total_texts: 2481, positive_count: 214, neutral_count: 88, negative_count: 40 },
    sources: [ALMARAI_FACEBOOK],
    narrative: {
      en: "Almarai's Ramadan product bundles are the dominant conversation driver this period, with sentiment strongly positive on pricing and perceived value. Product freshness continues to be the brand's most-cited strength across all post types. A small but recurring cluster of packaging complaints, concentrated on juice cartons, is worth monitoring by batch.",
      ar: "تُعد عروض منتجات رمضان من المراعي الدافع الأبرز للنقاش خلال هذه الفترة، مع مشاعر إيجابية قوية حول الأسعار والقيمة المتصورة. تبقى طازجية المنتجات أبرز نقاط قوة العلامة التجارية المذكورة عبر جميع أنواع المنشورات. توجد مجموعة صغيرة لكن متكررة من شكاوى التغليف، تتركز في عبوات العصير، وتستحق المتابعة حسب رقم الدفعة.",
    },
    highlights: {
      en: [
        "Ramadan family bundle pricing drove the highest engagement of any product line this quarter.",
        "Milk freshness and taste remain the single most-praised attribute across all sources.",
        "Juice carton seal complaints cluster around a small number of dates, suggesting a batch issue rather than a systemic one.",
      ],
      ar: [
        "حققت أسعار عبوة رمضان العائلية أعلى تفاعل لأي خط منتجات هذا الربع.",
        "تبقى طازجية وطعم الحليب أكثر السمات إشادةً عبر جميع المصادر.",
        "تتركز شكاوى إغلاق عبوات العصير حول عدد قليل من التواريخ، ما يشير إلى مشكلة دفعة إنتاج وليست مشكلة عامة.",
      ],
    },
    recommendedActions: {
      en: [
        "Cross-check juice carton complaint dates against production batch records to isolate the affected lot.",
        "Expand sugar-free product visibility in response to repeated customer requests.",
        "Extend Ramadan bundle messaging into a year-round 'family value pack' campaign given its engagement performance.",
      ],
      ar: [
        "مطابقة تواريخ شكاوى عبوات العصير مع سجلات دفعات الإنتاج لتحديد الدفعة المتأثرة.",
        "توسيع ظهور المنتجات الخالية من السكر استجابة للطلبات المتكررة من العملاء.",
        "تمديد رسائل عبوة رمضان إلى حملة 'عبوة القيمة العائلية' على مدار العام نظراً لأدائها القوي في التفاعل.",
      ],
    },
  },
  {
    id: -3,
    companyName: { en: "Al Baik vs Herfy", ar: "البيك مقابل هيرفي" },
    reportTitle: { en: "Al Baik vs Herfy Competitive Analysis", ar: "تحليل تنافسي: البيك مقابل هيرفي" },
    analysisType: "competitive_analysis",
    daysAgo: 8,
    dataQualityScore: 90,
    sentiment: { total_posts: 485, total_texts: 3610, positive_count: 352, neutral_count: 94, negative_count: 39 },
    sources: [ALBAIK_INSTAGRAM, HERFY_INSTAGRAM],
    llmComparison: {
      executive_summary: {
        en: "Al Baik commands markedly higher brand passion, driven almost entirely by its garlic sauce, and generates more total engagement per post than Herfy. Herfy holds a distinct edge on perceived value-for-money and loyalty program satisfaction, with fewer but steadier positive mentions. Al Baik's negative sentiment is concentrated in operational friction (queues, delivery temperature); Herfy's is concentrated in consistency across branches.",
        ar: "تحظى البيك بولاء عاطفي أعلى بشكل ملحوظ، مدفوعاً بشكل شبه كامل بصوص الثوم، وتحقق تفاعلاً إجمالياً أعلى لكل منشور مقارنة بهيرفي. تتفوق هيرفي بوضوح في القيمة المتصورة مقابل السعر ورضا برنامج الولاء، مع إشارات إيجابية أقل عدداً لكن أكثر ثباتاً. تتركز المشاعر السلبية لدى البيك في احتكاكات تشغيلية (الطوابير، حرارة التوصيل)؛ بينما تتركز لدى هيرفي في تفاوت الجودة بين الفروع.",
      },
      comparison_posts: {
        en: [
          "Al Baik: 298 posts analyzed, average 620 likes per post.",
          "Herfy: 187 posts analyzed, average 251 likes per post.",
        ],
        ar: [
          "البيك: تم تحليل ٢٩٨ منشوراً، بمتوسط ٦٢٠ إعجاباً لكل منشور.",
          "هيرفي: تم تحليل ١٨٧ منشوراً، بمتوسط ٢٥١ إعجاباً لكل منشور.",
        ],
      },
      comparison_comments: {
        en: [
          "Al Baik comments center overwhelmingly on the garlic sauce and new branch openings.",
          "Herfy comments focus on pricing comparisons and loyalty app rewards.",
        ],
        ar: [
          "تتمحور تعليقات البيك بشكل كبير حول صوص الثوم وافتتاحات الفروع الجديدة.",
          "تركز تعليقات هيرفي على مقارنات الأسعار ومكافآت تطبيق الولاء.",
        ],
      },
      comparison_metrics: {
        en: [
          "Al Baik positive sentiment: 77.5% vs Herfy: 64.7%.",
          "Al Baik negative sentiment: 6.4% vs Herfy: 10.7%.",
        ],
        ar: [
          "المشاعر الإيجابية لدى البيك: ٧٧.٥٪ مقابل هيرفي: ٦٤.٧٪.",
          "المشاعر السلبية لدى البيك: ٦.٤٪ مقابل هيرفي: ١٠.٧٪.",
        ],
      },
      risks: {
        en: [
          "Al Baik's queue-time complaints could erode weekend traffic if left unaddressed as new branches open.",
          "Herfy's branch-to-branch inconsistency risks diluting its value-for-money reputation.",
        ],
        ar: [
          "قد تؤدي شكاوى وقت الانتظار لدى البيك إلى تراجع الإقبال في نهاية الأسبوع إن لم تُعالج مع افتتاح فروع جديدة.",
          "يهدد تفاوت الجودة بين فروع هيرفي بإضعاف سمعتها في القيمة مقابل السعر.",
        ],
      },
      opportunities: {
        en: [
          "Al Baik could convert delivery-temperature complaints into a premium insulated-packaging upsell.",
          "Herfy could lean further into loyalty app rewards messaging, its strongest differentiator against Al Baik.",
        ],
        ar: [
          "يمكن للبيك تحويل شكاوى حرارة التوصيل إلى عرض تغليف معزول مميز.",
          "يمكن لهيرفي التركيز أكثر على رسائل مكافآت تطبيق الولاء، وهي أبرز نقطة تميزها عن البيك.",
        ],
      },
      recommendations: {
        en: [
          "Al Baik: pilot a dedicated delivery queue at high-traffic branches to separate it from drive-thru congestion.",
          "Herfy: standardize portion-size QA checks across franchise locations to close the consistency gap.",
        ],
        ar: [
          "البيك: تجربة طابور توصيل مخصص في الفروع كثيفة الحركة لفصله عن ازدحام السيارات.",
          "هيرفي: توحيد فحوصات جودة حجم الوجبات عبر فروع الامتياز لسد فجوة التفاوت.",
        ],
      },
    },
  },
  {
    id: -4,
    companyName: { en: "Jarir Bookstore", ar: "مكتبة جرير" },
    reportTitle: { en: "Jarir Bookstore Brand Analysis", ar: "تحليل علامة مكتبة جرير التجارية" },
    analysisType: "brand_analysis",
    daysAgo: 12,
    dataQualityScore: 88,
    sentiment: { total_posts: 203, total_texts: 1873, positive_count: 140, neutral_count: 45, negative_count: 18 },
    sources: [JARIR_TWITTER],
    narrative: {
      en: "Back-to-school season dominates Jarir conversation, with laptop bundle pricing and knowledgeable in-store tech support as the clearest positive drivers. A smaller volume of complaints centers on online delivery delays and stock-outs for high-demand GPU models, suggesting a supply-chain rather than a service issue.",
      ar: "يهيمن موسم العودة إلى المدارس على النقاش حول جرير، مع أسعار عروض اللابتوب والدعم الفني الواعي داخل الفروع كأبرز الدوافع الإيجابية. تتركز مجموعة أصغر من الشكاوى حول تأخر التوصيل الإلكتروني ونفاد مخزون بطاقات الشاشة كثيرة الطلب، ما يشير إلى مشكلة في سلسلة التوريد أكثر منها في الخدمة.",
    },
    highlights: {
      en: [
        "Laptop bundle deals are the single highest-engagement topic in the sample.",
        "In-store tech support is consistently described as knowledgeable, a clear differentiator.",
        "GPU stock-outs are driving delivery-delay complaints more than fulfillment issues.",
      ],
      ar: [
        "عروض حزم اللابتوب هي الموضوع الأعلى تفاعلاً في العينة.",
        "يوصف الدعم الفني داخل الفروع باستمرار بأنه واعٍ ومتمكن، وهي ميزة تنافسية واضحة.",
        "نفاد مخزون بطاقات الشاشة هو الدافع الأكبر لشكاوى تأخر التوصيل أكثر من مشاكل التنفيذ.",
      ],
    },
    recommendedActions: {
      en: [
        "Add a stock-availability indicator to popular GPU listings to set delivery expectations upfront.",
        "Extend back-to-school bundle promotions with a referral incentive given the high organic engagement.",
        "Highlight in-store tech support in marketing; it's an under-leveraged competitive advantage.",
      ],
      ar: [
        "إضافة مؤشر توفر المخزون لبطاقات الشاشة الرائجة لتحديد توقعات التوصيل مسبقاً.",
        "تمديد عروض موسم المدارس بحافز إحالة نظراً للتفاعل العضوي المرتفع.",
        "إبراز الدعم الفني داخل الفروع في التسويق، فهو ميزة تنافسية غير مستغلة بالكامل.",
      ],
    },
  },
  {
    id: -5,
    companyName: { en: "extra vs Jarir", ar: "اكسترا مقابل جرير" },
    reportTitle: { en: "extra vs Jarir Competitive Analysis", ar: "تحليل تنافسي: اكسترا مقابل جرير" },
    analysisType: "competitive_analysis",
    daysAgo: 15,
    dataQualityScore: 86,
    sentiment: { total_posts: 357, total_texts: 2790, positive_count: 234, neutral_count: 85, negative_count: 38 },
    sources: [EXTRA_FACEBOOK, JARIR_TWITTER],
    llmComparison: {
      executive_summary: {
        en: "Jarir generates higher engagement and more positive sentiment overall, anchored by its back-to-school tech authority and in-store support reputation. extra holds a clear edge in large-appliance financing conversation, where its installment plans are the most-cited positive theme. Both brands share a common friction point around delivery timing for high-demand or large-format items.",
        ar: "تحقق جرير تفاعلاً ومشاعر إيجابية أعلى بشكل عام، مرتكزة على مكانتها التقنية في موسم المدارس وسمعة الدعم داخل الفروع. تتفوق اكسترا بوضوح في نقاش تمويل الأجهزة الكبيرة، حيث تُعد خطط التقسيط الموضوع الإيجابي الأكثر ذكراً. تشترك العلامتان في نقطة احتكاك مشتركة حول توقيت التوصيل للمنتجات كثيرة الطلب أو كبيرة الحجم.",
      },
      comparison_posts: {
        en: [
          "Jarir: 203 posts analyzed, average 298 likes per post.",
          "extra: 154 posts analyzed, average 187 likes per post.",
        ],
        ar: [
          "جرير: تم تحليل ٢٠٣ منشورات، بمتوسط ٢٩٨ إعجاباً لكل منشور.",
          "اكسترا: تم تحليل ١٥٤ منشوراً، بمتوسط ١٨٧ إعجاباً لكل منشور.",
        ],
      },
      comparison_comments: {
        en: [
          "Jarir comments concentrate on laptop deals and technical advice requests.",
          "extra comments concentrate on installment terms and White Friday pricing.",
        ],
        ar: [
          "تتركز تعليقات جرير حول عروض اللابتوب وطلبات المشورة التقنية.",
          "تتركز تعليقات اكسترا حول شروط التقسيط وأسعار الجمعة البيضاء.",
        ],
      },
      comparison_metrics: {
        en: [
          "Jarir positive sentiment: 69.0% vs extra: 61.0%.",
          "Jarir negative sentiment: 8.9% vs extra: 13.0%.",
        ],
        ar: [
          "المشاعر الإيجابية لدى جرير: ٦٩.٠٪ مقابل اكسترا: ٦١.٠٪.",
          "المشاعر السلبية لدى جرير: ٨.٩٪ مقابل اكسترا: ١٣.٠٪.",
        ],
      },
      risks: {
        en: [
          "extra's slow warranty-claim processing could push large-appliance buyers toward Jarir's electronics range.",
          "Jarir's GPU stock-outs could push budget-conscious buyers toward extra's promotional pricing.",
        ],
        ar: [
          "قد تدفع بطء معالجة مطالبات الضمان لدى اكسترا مشتري الأجهزة الكبيرة نحو تشكيلة جرير الإلكترونية.",
          "قد يدفع نفاد مخزون بطاقات الشاشة لدى جرير المشترين الحريصين على الميزانية نحو أسعار اكسترا الترويجية.",
        ],
      },
      opportunities: {
        en: [
          "extra could co-market installment plans alongside Jarir-style tech-support messaging to close the trust gap.",
          "Jarir could introduce appliance financing options, an area where extra currently leads unopposed.",
        ],
        ar: [
          "يمكن لاكسترا الترويج المشترك لخطط التقسيط مع رسائل دعم فني على غرار جرير لسد فجوة الثقة.",
          "يمكن لجرير تقديم خيارات تمويل للأجهزة، وهو مجال تتصدره اكسترا حالياً دون منافسة.",
        ],
      },
      recommendations: {
        en: [
          "extra: publish a warranty-claim status tracker to reduce the volume of processing-time complaints.",
          "Jarir: pre-order GPU stock ahead of back-to-school demand spikes based on prior-year sell-through data.",
        ],
        ar: [
          "اكسترا: نشر أداة تتبع حالة مطالبات الضمان لتقليل حجم شكاوى وقت المعالجة.",
          "جرير: طلب مخزون بطاقات الشاشة مسبقاً قبل ذروة طلب موسم المدارس استناداً إلى بيانات المبيعات السابقة.",
        ],
      },
    },
  },
];

const buildDetails = (seed: DemoReportSeed, lang: DemoLang): SocialListeningAnalysisDetails => {
  const total = seed.sentiment.positive_count + seed.sentiment.neutral_count + seed.sentiment.negative_count;
  const now = Date.now();
  const createdAt = new Date(now - seed.daysAgo * 24 * 60 * 60 * 1000).toISOString();

  return {
    analysis_id: seed.id,
    analysis_type: seed.analysisType === "brand_analysis" ? "brand" : "competitive",
    company_name: seed.companyName[lang],
    status: "completed",
    sentiment_method: "transformer-based multilingual classifier",
    translation_method: "auto",
    data_quality_score: seed.dataQualityScore,
    sources_analyzed: seed.sources.map(source => source.source_name),
    overall_sentiment: {
      total_texts: seed.sentiment.total_texts,
      total_posts: seed.sentiment.total_posts,
      total_count: total,
      positive_count: seed.sentiment.positive_count,
      positive_percentage: pct(seed.sentiment.positive_count, total),
      neutral_count: seed.sentiment.neutral_count,
      neutral_percentage: pct(seed.sentiment.neutral_count, total),
      negative_count: seed.sentiment.negative_count,
      negative_percentage: pct(seed.sentiment.negative_count, total),
    },
    narrative_summary: seed.narrative?.[lang] ?? "",
    executive_summary: seed.narrative?.[lang] ?? seed.llmComparison?.executive_summary[lang] ?? "",
    key_insights: seed.highlights?.[lang] ?? [],
    highlights: seed.highlights?.[lang],
    recommended_actions:
      seed.recommendedActions?.[lang] ?? seed.llmComparison?.recommendations[lang] ?? [],
    source_breakdown: seed.sources.map(source => buildSourceBreakdown(source, lang)),
    analysis_date: createdAt,
    completed_at: createdAt,
    created_at: createdAt,
    llm_comparison: seed.llmComparison
      ? {
          executive_summary: seed.llmComparison.executive_summary[lang],
          comparison_posts: seed.llmComparison.comparison_posts[lang],
          comparison_comments: seed.llmComparison.comparison_comments[lang],
          comparison_metrics: seed.llmComparison.comparison_metrics[lang],
          risks: seed.llmComparison.risks[lang],
          opportunities: seed.llmComparison.opportunities[lang],
          recommendations: seed.llmComparison.recommendations[lang],
        }
      : undefined,
  };
};

/** Full report detail for a demo id, or `null` if `id` isn't one of the demo reports. */
export function getDemoAnalysisDetails(
  id: number,
  language: string | undefined,
): SocialListeningAnalysisDetails | null {
  const seed = REPORT_SEEDS.find(item => item.id === id);
  if (!seed) {
    return null;
  }
  return buildDetails(seed, resolveLang(language));
}

/** History-list rows for all demo reports, in the current UI language. */
export function getDemoSocialListeningHistory(
  language: string | undefined,
): SocialListeningHistoryItem[] {
  const lang = resolveLang(language);
  const now = Date.now();
  return REPORT_SEEDS.map(seed => {
    const createdAt = new Date(now - seed.daysAgo * 24 * 60 * 60 * 1000).toISOString();
    return {
      analysis_id: seed.id,
      company_name: seed.companyName[lang],
      report_title: seed.reportTitle[lang],
      author_name: "DataTram AI",
      created_at: createdAt,
      updated_at: createdAt,
      status: "completed",
      analysis_type: seed.analysisType,
    } satisfies SocialListeningHistoryItem;
  });
}

export function isDemoAnalysisId(id: number): boolean {
  return (DEMO_SOCIAL_LISTENING_IDS as readonly number[]).includes(id);
}
